import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, User, Building2, Phone, FileText, X } from 'lucide-react';
import { appointmentsAPI, usersAPI } from '../../../lib/api.js';
import { useCompanies } from '../../../hooks/useSharedData.js';
import Card from '../../../components/ui/Card.jsx';
import Button from '../../../components/ui/Button.jsx';
import Input from '../../../components/ui/Input.jsx';
import Badge from '../../../components/ui/Badge.jsx';
import RichTextEditor from '../../../components/ui/RichTextEditor.jsx';

// Helper function to format phone number for display (show only 10 digits)
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it's 12 digits and starts with 91, show only the last 10 digits
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    return digitsOnly.substring(2);
  }
  
  // If it's 10 digits, show as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  
  // For other cases, return the original
  return phoneNumber;
};

// Helper function to get badge variant based on status


export const OrderlyRoomForm = ({ onClose, onSuccess, editAppointment, currentUser }) => {
  // Check if user can edit
  const canEdit = !editAppointment;
  const [formData, setFormData] = useState(() => {
     if (editAppointment) {
      return {
        ...editAppointment,
        phone_number: formatPhoneForDisplay(editAppointment.phone_number) || '',
        request_type: 'orderly room',
        status: 'scheduled',
      };
    }
    
    return {
      // Basic Info
      company_id: '',
      user_id: '',
      badge_number: '',
      request_type: 'orderly room',
       appointment_date: '',
      appointment_time: '12:00:00',
      status: 'scheduled',
      additional_details: '',
      notes: '',
      clerk_remarks: '',
      preferred_date: '',
      
      // User Info
      phone_number: '',
      email: '',
      first_name: '',
      last_name: '',
      middle_name: '',
      full_name: '',
      icard_no: '',
      dgp_id: '',
      role_id: '',
      designation_id: '',
      designation_name: '',
      whatsapp_enabled: false,
      whatsapp_language: 'en',
      
      
      // System Fields
      auto_scheduled: false,
      original_request_date: '',
    };
  });

  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState('');

  const queryClient = useQueryClient();

  // Fetch companies and users
  const { data: companies, isLoading: companiesLoading } = useCompanies();
  // Load initial users (small cached list)
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const response = await usersAPI.getAll();
        const usersData = response.data.data || [];
        return usersData;
      } catch (error) {
        console.error('Error fetching users:', error);
        return [];
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Server-side search as-you-type
  const { data: searchedUsers, isFetching: searchedUsersFetching } = useQuery({
    queryKey: ['users', 'search', userSearchQuery?.toLowerCase()?.trim()],
    enabled: !!userSearchQuery && userSearchQuery.trim().length >= 2,
    queryFn: async () => {
      try {
        const response = await usersAPI.getAll({ search: userSearchQuery.trim().toLowerCase() });
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Error searching users:', error);
        return [];
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => appointmentsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments']);
      alert('Appointment created successfully!');
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Error creating appointment:', error);
      
      // Handle specific validation errors from backend
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        if (errorData.message) {
          alert(`Error: ${errorData.message}`);
        } else {
          alert('Failed to create appointment. Please try again.');
        }
      } else {
        alert('Failed to create appointment. Please try again.');
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => appointmentsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments']);
      alert('Appointment updated successfully!');
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Error updating appointment:', error);
      
      // Handle specific validation errors from backend
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        if (errorData.message) {
          alert(`Error: ${errorData.message}`);
        } else {
          alert('Failed to update appointment. Please try again.');
        }
      } else {
        alert('Failed to update appointment. Please try again.');
      }
    },
  });

  // Reschedule mutation (edit only)
  const rescheduleMutation = useMutation({
    mutationFn: ({ id, data }) => appointmentsAPI.reschedule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments']);
      alert('Appointment rescheduled successfully!');
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Error rescheduling appointment:', error);
      alert('Failed to reschedule appointment. Please try again.');
    },
  });

  // Effect to populate form when editing
  useEffect(() => {
    if (editAppointment) {
      setFormData({
        ...editAppointment,
        phone_number: formatPhoneForDisplay(editAppointment.phone_number) || '',
        request_type: 'orderly room',
        status:  'completed' || editAppointment.status  // Default to 'completed' when editing
      });
      setSelectedUserName(editAppointment.full_name || `${editAppointment.first_name} ${editAppointment.last_name}`);
      setUserSearchQuery(editAppointment.full_name || `${editAppointment.first_name} ${editAppointment.last_name}`);
    }
  }, [editAppointment]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.company_id || !formData.user_id) {
      alert('Please fill in all required fields: Company and User');
      return;
    }

    // Validate additional_details (Purpose of Visit) for orderly room appointments
    if (!formData.additional_details || formData.additional_details.trim().length === 0) {
      alert('Please fill required field: Purpose of Visit (भेटीचा उद्देश)');
      return;
    }

    // Helper function to remove undefined values
    const cleanData = (obj) => {
      const cleaned = {};
      Object.keys(obj).forEach(key => {
        if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') {
          cleaned[key] = obj[key];
        }
      });
      return cleaned;
    };

     // Simple status update
     const nextStatus = formData.status;

    const normalizeDate = (v) => {
      if (!v) return undefined;
      if (v instanceof Date) return v.toISOString().split('T')[0];
      if (typeof v === 'string') return v.includes('T') ? v.split('T')[0] : v;
      return v;
    };

    const origAppointmentDate = editAppointment ? normalizeDate(editAppointment.appointment_date) : undefined;
    const origOriginalRequestDate = editAppointment ? normalizeDate(editAppointment.original_request_date) : undefined;

    const submitData = cleanData({
      company_id: parseInt(formData.company_id),
      user_id: parseInt(formData.user_id),
      badge_number: formData.badge_number ? parseInt(formData.badge_number) : undefined,
      request_type: formData.request_type,
      additional_details: formData.additional_details,
      notes: formData.notes,
      clerk_remarks: formData.clerk_remarks,
      preferred_date: formData.preferred_date,
      
       // Status
       status: nextStatus,
      
      ...(editAppointment && {
        ...(normalizeDate(formData.appointment_date) && normalizeDate(formData.appointment_date) !== origAppointmentDate
          ? { appointment_date: normalizeDate(formData.appointment_date) }
          : {}),
        ...(formData.appointment_time && formData.appointment_time !== editAppointment.appointment_time
          ? { appointment_time: formData.appointment_time }
          : {}),
        ...(normalizeDate(formData.original_request_date) && normalizeDate(formData.original_request_date) !== origOriginalRequestDate
          ? { original_request_date: normalizeDate(formData.original_request_date) }
          : {}),
        ...(formData.notes !== undefined ? { notes: formData.notes } : {})
      })
    });

    try {
      if (editAppointment) {
        // If status set to rescheduled and no explicit date provided, auto-reschedule via backend
        if (submitData.status === 'rescheduled' && !submitData.appointment_date) {
          await rescheduleMutation.mutateAsync({ id: editAppointment.id, data: { auto_reschedule: true } });
        } else {
          await updateMutation.mutateAsync({ id: editAppointment.id, data: submitData });
        }
      } else {
        await createMutation.mutateAsync(submitData);
      }
    } catch (error) {
      console.error('Error submitting appointment:', error);
      
      // Handle specific validation errors from backend
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        
        if (errorData.details) {
          // Multiple field validation errors
          const errorMessages = Object.entries(errorData.details)
            .map(([field, message]) => `${field}: ${message}`)
            .join('\n');
          alert(`Validation errors:\n${errorMessages}`);
        } else if (errorData.field && errorData.message) {
          // Single field validation error
          alert(`Validation error: ${errorData.message}`);
        } else {
          // Generic error message
          alert(`Error: ${errorData.message || 'Failed to submit appointment. Please try again.'}`);
        }
      } else {
        // Fallback for network errors or other issues
        alert('Error submitting appointment. Please try again.');
      }
    }
  };

  // Function to find user by phone number
  const findUserByPhoneNumber = async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length < 10) return;

    try {
      const response = await appointmentsAPI.findUserByPhone(phoneNumber);
      if (response.data.success && response.data.user) {
        const user = response.data.user;
        setFormData(prev => ({
          ...prev,
          user_id: user.id,
          company_id: user.company_id,
          badge_number: user.badge_no || user.badge_number || '',
          phone_number: formatPhoneForDisplay(user.phone_number) || '',
        }));
      }
    } catch (error) {
      console.error('Error finding user by phone number:', error);
    }
  };

  const getFilteredUsers = () => {
    const query = userSearchQuery?.trim();
    if (!query) return [];
    
    const q = query.toLowerCase();
    
    // Use server results when query length >= 2 and server search has results
    if (query.length >= 2 && Array.isArray(searchedUsers) && searchedUsers.length > 0) {
      return searchedUsers.slice(0, 8);
    }
    
    // Fall back to local search for queries < 2 chars or when server search has no results
    if (!Array.isArray(users) || users.length === 0) return [];
    
    return users.filter(user => {
      if (!user) return false;
      const searchFields = [
        user.first_name,
        user.last_name,
        user.full_name,
        user.email,
        user.phone_number,
        String(user.badge_number || user.badge_no || '')
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchFields.includes(q);
    }).slice(0, 8);
  };

  // Update handleUserSearchChange to show suggestions immediately
  const handleUserSearchChange = (e) => {
    const query = e.target.value;
    setUserSearchQuery(query);
    // Always show suggestions when typing
    setShowUserSuggestions(true);
    
    if (query.length === 0) {
      setFormData(prev => ({
        ...prev,
        user_id: '',
        company_id: '',
        badge_number: '',
        phone_number: '',
        email: '',
        role_id: '',
        whatsapp_enabled: false,
        whatsapp_language: 'en',
      }));
      setSelectedUserName('');
    }
  };

  // Function to handle user selection from search
  const handleUserSelect = (user) => {
    console.log('Selected user:', user); // Add this for debugging
    setFormData(prev => ({
      ...prev,
      user_id: user.id,
      company_id: user.company_id || '',
      badge_number: user.badge_no || user.badge_number || '',
      phone_number: formatPhoneForDisplay(user.phone_number) || '',
      first_name: user.first_name || '',
      middle_name: user.middle_name || '',
      last_name: user.last_name || '',
      full_name: user.full_name || `${user.first_name} ${user.last_name}`,
      email: user.email || '',
      icard_no: user.icard_no || '',
      dgp_id: user.dgp_id || '',
      role_id: user.role_id || '',
      designation_id: user.designation_id || '',
      designation_name: user.designation_name || '',
    }));
    
    setSelectedUserName(user.full_name || `${user.first_name} ${user.last_name}`);
    setUserSearchQuery(user.full_name || `${user.first_name} ${user.last_name}`);
    setShowUserSuggestions(false);
  };

  

  // Function to handle Enter key press for user selection
  const handleUserSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filteredUsers = getFilteredUsers();
      if (filteredUsers.length > 0) {
        handleUserSelect(filteredUsers[0]);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Personal Information Section */}
      <div className="relative">
        <div className="relative bg-white dark:bg-gray-800 p-6 rounded-2xl border border-blue-200/50 dark:border-blue-700/50 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Personal Information</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* User Search */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <User className="w-4 h-4" />
                Search User
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={userSearchQuery}
                  onChange={handleUserSearchChange}
                  onKeyDown={handleUserSearchKeyDown}
                  onFocus={() => {
                    if (userSearchQuery.trim().length > 0) {
                      setShowUserSuggestions(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowUserSuggestions(false), 200)}
                  placeholder="Type user name to search..."
                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                  required
                />
                {showUserSuggestions && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {userSearchQuery.trim().length >= 2 && searchedUsersFetching ? (
                      <div className="px-4 py-3 text-gray-500">
                        Searching...
                      </div>
                    ) : (
                      <>
                        {getFilteredUsers().map((user) => (
                          <div
                            key={user.id}
                            onClick={() => handleUserSelect(user)}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          >
                            <div className="font-medium">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email} • Badge: {user.badge_number || user.badge_no}
                            </div>
                          </div>
                        ))}
                        {getFilteredUsers().length === 0 && userSearchQuery.trim().length > 0 && (
                          <div className="px-4 py-3 text-gray-500">
                            {userSearchQuery.trim().length < 2 
                              ? 'Type at least 2 characters to search...' 
                              : 'No users found'}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number
              </label>
              <input
                value={formData.phone_number}
                onChange={(e) => {
                  const phoneNumber = e.target.value;
                  setFormData(prev => ({ ...prev, phone_number: phoneNumber }));
                  if (phoneNumber.length >= 10) {
                    findUserByPhoneNumber(phoneNumber);
                  }
                }}
                placeholder="Enter phone number..."
                className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                required
              />
            </div>

            {/* Company Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Company
              </label>
              <select
                value={formData.company_id}
                onChange={(e) => setFormData(prev => ({ ...prev, company_id: e.target.value }))}
                className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                required
              >
                <option value="">Select company...</option>
                {!companiesLoading && companies?.data?.data?.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
              </select>
            </div>

            {/* Read-only Fields */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Badge Number</label>
              <input
                value={formData.badge_number}
                readOnly
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Email</label>
              <input
                value={formData.email}
                readOnly
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Role</label>
              <input
                value={formData.role_id}
                readOnly
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Details Section */}
      

       {/* Additional Details Section */}
       <div className="relative">
         <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-violet-500/10 rounded-2xl blur-xl"></div>
         <div className="relative bg-white dark:bg-gray-800 p-6 rounded-2xl border border-purple-200/50 dark:border-purple-700/50 shadow-lg">
           <div className="flex items-center justify-between mb-4">
             <div className="flex items-center gap-3">
               <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                 <FileText className="w-4 h-4 text-white" />
               </div>
               <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Additional Details</h3>
             </div>
           </div>

           <div className="space-y-6">
             {/* Purpose of Visit */}
             <div className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                   भेटीचा उद्देश (Purpose of Visit) <span className="text-red-500">*</span>
                 </label>
                 <textarea
                   value={formData.additional_details || ''}
                   onChange={(e) => setFormData(prev => ({ ...prev, additional_details: e.target.value }))}
                   rows={3}
                   className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl font-dvtt-input"
                   placeholder="Enter purpose of visit..."
                   required
                 />
               </div>
               {/* Clerk Remarks */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                   क्लर्क टिप्पणी (Clerk Remarks)
                 </label>
                 <div className="font-dvtt-input">
                   <RichTextEditor
                     value={formData.clerk_remarks || ''}
                     onChange={(content) => setFormData(prev => ({ ...prev, clerk_remarks: content }))}
                     placeholder="Enter clerk remarks..."
                   />
                 </div>
                 <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                   Clerk remarks will be saved with processing information
                 </p>
               </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Commandants Order
                </label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl"
                  placeholder="Add any internal notes..."
                />
              </div>
              
              
             </div>
           </div>
         </div>
       </div>

      {/* Form Actions and Status */}
      <div className="flex items-center justify-between pt-4 border-t">
        {/* Status Dropdown */}
        <div className="w-64">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
            className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
            required
          >
            <option value="scheduled">📅 Scheduled</option>
            <option value="clerk_processed">📝 Clerk Processed</option>
            <option value="completed">✅ Completed</option>
            <option value="cancelled">❌ Cancelled</option>
            <option value="rescheduled">🔄 Rescheduled</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="flex items-center space-x-2"
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{editAppointment ? 'Updating...' : 'Creating...'}</span>
              </>
            ) : (
              <span>{editAppointment ? 'Update Appointment' : 'Create Appointment'}</span>
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default OrderlyRoomForm;